/**
 * Reusable picker for selecting a credential.
 *
 * Filters by ``type`` (or list of accepted types) so e.g. an SMTP
 * channel only sees SMTP credentials, an HTTP sink sees http_bearer
 * + api_key, etc.
 *
 * A `tray_gateway` credential (a pointer to a DB behind an on-prem agent,
 * ADR-0042) is a JOLLY for any DB-typed picker: it satisfies a `postgres`/
 * `mysql`/`sqlserver` requirement because the agent runs the query on whatever
 * remote dialect it's bound to. So every DB dial-site picker (ETL DB blocks,
 * the workflow `postgres` node) shows gateway credentials alongside direct
 * ones — selecting one routes the connection through the agent, no per-site
 * change. This is the FE half of the slice-2 keystone ("the credential is the
 * gateway seam").
 *
 * Built on the existing ``Combobox`` for keyboard nav, search + the
 * proper Radix Popover behaviour inside dialogs.
 */
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  credentialsApi,
  type CredentialType,
} from "@/lib/api-credentials";

/** Credential types that name an external DB — for these a `tray_gateway`
 *  pointer is a valid alternative (the agent dials the real dialect). Exported
 *  so a picker can expand a dialect-neutral `"db"` hint (ETL DB blocks) to the
 *  full set. */
export const DB_CREDENTIAL_TYPES: CredentialType[] = ["postgres", "mysql", "sqlserver"];

/** The effective accepted-types for a picker: the requested type(s), PLUS
 *  `tray_gateway` when a DB type is requested (so on-prem-gateway credentials
 *  show in every DB dial-site picker). `undefined` = accept any. Pure — pinned
 *  by the picker test. */
export function acceptedCredentialTypes(
  type?: CredentialType | CredentialType[],
): CredentialType[] | undefined {
  if (!type) return undefined;
  const list = Array.isArray(type) ? type : [type];
  const acceptsDb = list.some((t) => DB_CREDENTIAL_TYPES.includes(t));
  return acceptsDb && !list.includes("tray_gateway")
    ? [...list, "tray_gateway" as CredentialType]
    : list;
}


export interface CredentialPickerProps {
  value: string | null;
  onChange: (id: string | null) => void;
  /** Filter to credentials of these type(s). String OR array OR omit
   *  for "any". */
  type?: CredentialType | CredentialType[];
  /** Override the empty-state hint, e.g. "No SMTP credentials —
   *  create one first". */
  emptyText?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}


export function CredentialPicker({
  value,
  onChange,
  type,
  emptyText,
  placeholder,
  disabled,
  id,
}: CredentialPickerProps) {
  // A DB-typed picker ALSO accepts a `tray_gateway` pointer (ADR-0042) — the agent
  // dials the real remote dialect — so gateway credentials show in DB dial-site pickers.
  const accepted = React.useMemo(() => acceptedCredentialTypes(type), [type]);

  // Query a single type at a time (backend supports one ?type=).
  // For multi-type, fetch all and filter client-side — credentials
  // are bounded (<100s) so the cost is negligible.
  const q = useQuery({
    queryKey: ["credentials", "picker", accepted?.join(",") ?? "all"],
    queryFn: () =>
      credentialsApi.list(
        accepted && accepted.length === 1 ? { type: accepted[0], enabled: true } : { enabled: true },
      ),
    staleTime: 30_000,
  });

  const options = React.useMemo<ComboboxOption[]>(() => {
    const rows = q.data ?? [];
    const filtered = accepted && accepted.length > 1
      ? rows.filter((c) => accepted.includes(c.type))
      : rows;
    return filtered.map((c) => ({
      value: c.id,
      label: c.name,
      description: `${c.type}${c.description ? ` · ${c.description}` : ""}`,
    }));
  }, [q.data, accepted]);

  const fallbackEmpty =
    accepted && accepted.length > 0
      ? `No ${accepted.join("/")} credentials — create one in /credentials.`
      : "No credentials.";

  return (
    <Combobox
      id={id}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder ?? (q.isLoading ? "loading…" : "Pick a credential")}
      loading={q.isLoading}
      emptyText={emptyText ?? fallbackEmpty}
      disabled={disabled}
    />
  );
}
